/*
  shintop-guide-toc — the guide's table of contents.

  Round 12 of the Guides-article surface, 2026-09-07. The measured problem: the imagawayaki
  guide is 14,189px at 1440 and 19,957px at 375, with nine <h2> sections spaced ~1,000-1,200px
  apart and no way to see the shape of the page or how much of it is left. On desktop the rail
  goes in the 349px of gutter the 726px text column was already leaving empty each side.

  Built from the DOM rather than authored, deliberately: every guide's <h2> already carries an
  id (checked: 9 of 9 on this corpus), so a corpus-wide table of contents costs zero words in
  nine body files and can never drift from the headings it points at.

  Progressive enhancement, in the Dawn sense: the element ships with the `hidden` attribute and
  only removes it once it has actually built a list. No JS, or an article with no guide body,
  renders nothing at all rather than an empty box.
*/
class ShintopGuideToc extends HTMLElement {
  connectedCallback() {
    // ⚠️ The script tag sits INSIDE this section, before the content div, so the element is
    // parsed and upgraded BEFORE `{{ article.content }}` exists in the DOM. The first build
    // of this file queried the headings here and found zero -- no error, no console entry,
    // just an element that stayed `hidden` and a grid that then put the article in the rail's
    // column. Wait for the parser before reading anything outside this element.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.build(), { once: true });
    } else {
      this.build();
    }
  }

  build() {
    const list = this.querySelector('.shintop-guide-toc__list');
    const box = this.querySelector('.shintop-guide-toc__box');
    if (!list || !box) return;

    // Sources is listed again (2026-09-08). r12b dropped it because the rail was "too wordy"
    // and a citation list is not a destination; with short `data-toc` labels it costs one line,
    // and both mock rails Naoya endorsed carry "08 Sources & credits".
    const heads = Array.from(document.querySelectorAll('.shintop-guide h2[id]'));
    // Two or three entries is a label, not a map — the same reasoning that stops the guide
    // index rendering a topic filter below two topics (ledger, 2026-08-29).
    if (heads.length < 4) return;

    const links = heads.map((h) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      // The label is a SPAN, not a bare text node. The entry is a two-column grid -- numeral
      // in the margin, label beside it -- and a grid container makes an item of every child,
      // so loose text would split into one item per run the moment a heading contained an
      // inline element (§4 rule 32). Two elements, always two items.
      const label = document.createElement('span');
      label.className = 'shintop-guide-toc__label';
      // `data-toc` on the heading is the SHORT rail label — "The three big chains" against a
      // heading of "Which konbini were you in?" (Naoya, 2026-09-08, choosing the mock's rail
      // over the derived one). It lives on the h2 itself rather than in a metafield so it
      // cannot drift from the heading it names, and it stays OPTIONAL: a guide that has not
      // been given labels yet falls back to the heading and reads exactly as it did before.
      label.textContent = (h.dataset.toc || '').trim() || h.textContent.trim();
      a.appendChild(label);
      li.appendChild(a);
      list.appendChild(li);
      return a;
    });

    this.hidden = false;

    // On the wide layout the rail is always open and its summary is hidden by CSS; below that
    // breakpoint it is a collapsed "On this page" the reader opens. Re-applied on change so a
    // resize into the wide layout cannot leave a railed-but-collapsed box.
    const wide = window.matchMedia('(min-width: 1375px)');
    const openWhenWide = () => {
      if (wide.matches) box.open = true;
    };
    openWhenWide();
    wide.addEventListener('change', openWhenWide);
    // S03-11 (audit 2026-09-13, chunk 9): kept so disconnectedCallback can take it off again.
    // A MediaQueryList is owned by the browser, not by this element, so a listener left on it
    // survives the element and keeps a closure over the whole rail alive.
    this.wide = wide;
    this.openWhenWide = openWhenWide;
    // On the rail the summary is a LABEL, not a control -- it is shown so a numbered list in
    // the margin cannot be misread as a sequence of steps. <details> still toggles on click or
    // Enter, and a collapsed rail would be a dead 200px column, so the toggle is refused while
    // the wide layout is on. Below the breakpoint it is a real control again.
    box.addEventListener('toggle', () => {
      if (wide.matches && !box.open) box.open = true;
    });

    // Where am I: half of what makes a long page tiring is losing your place. A hint only — the
    // list still works as a set of jumps whether or not this ever marks anything.
    //
    // S03-009 (audit 2026-10-14, chunk 15). This was an IntersectionObserver that remembered
    // every heading it had seen pass above the band and marked the last of them. An observer
    // reports a heading only when it CROSSES the band, so a jump that carries a heading from
    // above the band to below it in one step -- a click on an earlier entry, "Back to top" --
    // reports nothing: after reading to chapter 8, a click on entry 2 left 8 marked, and so did
    // "Back to top". The current entry is now read from geometry on each scroll frame, and
    // nothing is remembered between frames, so nothing can go stale.
    //
    // H03-12, same chunk: a heading counts as reached once its top passes a quarter of the
    // viewport OR its own scroll-margin-top, whichever is lower on the screen. A jump lands the
    // heading AT its scroll margin (138px at 1440), and on a short window that is below the
    // quarter line (125px at 500 tall, 97px on a landscape phone) -- the quarter alone marked
    // the chapter before the one the reader had just clicked. The margin is read live because
    // it carries Dawn's `--header-height`, which the header sets at its own time. The 2px is
    // the sub-pixel a jump can land past its margin by.
    let queued = false;
    const mark = () => {
      queued = false;
      let current = -1;
      heads.forEach((h, i) => {
        const margin = parseFloat(getComputedStyle(h).scrollMarginTop) || 0;
        if (h.getBoundingClientRect().top <= Math.max(window.innerHeight * 0.25, margin + 2)) current = i;
      });
      links.forEach((a, i) => a.classList.toggle('is-current', i === current));
    };
    const request = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(mark);
      }
    };
    // Kept for disconnectedCallback: both listeners are on `window`, which outlives the element.
    this.request = request;
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    mark();
  }

  /*
    S03-11 (audit 2026-09-13, chunk 9). This element survived a Theme Editor section
    replacement with a media-query listener and an IntersectionObserver still attached — it
    had no teardown at all. Both belong to objects that outlive this element (the media query
    list; the window, since chunk 15 of the 2026-10-14 audit replaced the observer with a
    scroll listener), so neither falls away when the element does.

    Never a shopper: a page load discards everything. It is the editor, where sections are
    replaced under a live document, and each replacement left another copy running.
  */
  disconnectedCallback() {
    this.wide?.removeEventListener('change', this.openWhenWide);
    if (this.request) {
      window.removeEventListener('scroll', this.request);
      window.removeEventListener('resize', this.request);
    }
  }
}

if (!customElements.get('shintop-guide-toc')) {
  customElements.define('shintop-guide-toc', ShintopGuideToc);
}

/*
  shintop-guide-progress — how much of the guide is left.

  The rail answers "where am I" on the desktop and is a collapsed summary below 1375px. But
  the phone is where this page is LONGEST — 19,957px at 375 against 12,410px at 1440 — so the
  width that needed the signal most was the one with no rail on screen. A 3px bar fixed to the
  top of the viewport works at every width and costs no layout.

  It measures the ARTICLE, not the document: the footer, the shoppable band and the trust
  block are not reading, and counting them would report a reader as three-quarters done while
  a quarter of the prose was still ahead of them.

  Progressive enhancement in the same shape as the rail above: ships `hidden`, and only
  reveals itself once it has found a guide body to measure. No JS, or a non-guide article,
  renders nothing at all.
*/
class ShintopGuideProgress extends HTMLElement {
  connectedCallback() {
    // Same parser problem the rail has: this element is in the section markup ABOVE
    // `{{ article.content }}`, so `.shintop-guide` does not exist yet at upgrade time.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.start(), { once: true });
    } else {
      this.start();
    }
  }

  start() {
    const body = document.querySelector('.shintop-guide');
    const fill = this.querySelector('.shintop-guide-progress__fill');
    if (!body || !fill) return;
    this.hidden = false;

    // "N min left in this guide" for the rail. Same source and the SAME DIVISOR as the hero's
    // "N min read" (`shintop-guide-hero.liquid`: words | divided_by: 200) — two different
    // divisors would put two contradictory reading times on one page, and the reader would be
    // right to trust neither.
    const status = document.querySelector('.shintop-guide-toc__status');
    const total = Math.max(1, Math.floor(body.textContent.trim().split(/\s+/).length / 200));

    let queued = false;
    const update = () => {
      queued = false;
      const top = body.getBoundingClientRect().top + window.scrollY;
      // Clamped at 1 so a guide shorter than the viewport divides by a real number rather
      // than reporting NaN as a transform and silently dropping the declaration.
      const distance = Math.max(1, body.offsetHeight - window.innerHeight);
      const passed = (window.scrollY - top) / distance;
      const fraction = Math.min(1, Math.max(0, passed));
      fill.style.transform = `scaleX(${fraction})`;
      if (status) {
        // ⚠️ SILENT UNTIL THE READER HAS ACTUALLY MOVED. At scroll 0 this said "11 min left in
        // this guide" while the hero byline 850px above it on the same screen said "11 min
        // read" — the same number twice, and the second one claiming to be a countdown that
        // had not counted anything. A progress readout that has no progress to report is
        // noise; `:empty` in the stylesheet drops its margin so nothing reserves space.
        const left = Math.ceil(total * (1 - fraction));
        if (fraction <= 0) {
          status.textContent = '';
        } else {
          status.textContent =
            left < 1 ? status.dataset.done || '' : (status.dataset.left || '').replace('__N__', String(left));
        }
      }
    };
    const request = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    };

    // S03-11: kept for disconnectedCallback. These are on `window`, which outlives the
    // element, so without a teardown every Theme Editor section replacement left another
    // scroll handler measuring a detached progress bar on every frame of every scroll.
    this.request = request;
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    update();
  }

  disconnectedCallback() {
    if (!this.request) return;
    window.removeEventListener('scroll', this.request);
    window.removeEventListener('resize', this.request);
  }
}

if (!customElements.get('shintop-guide-progress')) {
  customElements.define('shintop-guide-progress', ShintopGuideProgress);
}
